import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Trash2, ArrowUp, ArrowDown, Package } from "lucide-react";
import { itemSubtotal, type PricingMode } from "@/lib/budgetCalc";
import { itemFromServiceType, blankItem, blankRecurringItem } from "@/lib/budgetItems";
import type { ServiceType, BudgetItemInput } from "@/hooks/useBudgets";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v);

interface Props {
  items: BudgetItemInput[];
  onChange: (items: BudgetItemInput[]) => void;
  serviceTypes: ServiceType[];
  fallbackRate: number;
  disabled?: boolean;
}

/**
 * The "Serviços do pacote" block of the proposal editor: one row per item,
 * each priced by hours × rate or a fixed price. Adding a service type
 * prefills hours/rate from the catalog; custom rows start blank.
 */
export function BudgetItemsEditor({
  items,
  onChange,
  serviceTypes,
  fallbackRate,
  disabled,
}: Props) {
  const update = (idx: number, patch: Partial<BudgetItemInput>) =>
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const usedTypeIds = new Set(items.map((i) => i.service_type_id).filter(Boolean));
  const available = serviceTypes.filter((st) => !usedTypeIds.has(st.id));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium leading-none">
          <Package className="h-3.5 w-3.5 text-muted-foreground" /> Serviços do pacote
        </span>
        {!disabled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm" variant="outline" className="h-8 text-xs">
                <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar serviço
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
              {available.map((st) => (
                <DropdownMenuItem
                  key={st.id}
                  onClick={() => onChange([...items, itemFromServiceType(st, fallbackRate)])}
                >
                  {st.name}
                  <span className="ml-auto pl-4 text-[11px] text-muted-foreground">
                    {st.hours_min > 0 ? `${st.hours_min}h · ` : ""}
                    {fmt(st.default_hourly_rate)}/h
                  </span>
                </DropdownMenuItem>
              ))}
              {available.length === 0 && (
                <DropdownMenuItem disabled>Todos os tipos já estão no pacote</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onChange([...items, blankItem(fallbackRate)])}>
                Item personalizado…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onChange([...items, blankRecurringItem()])}>
                Mensalidade…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {items.length === 0 && (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          Nenhum serviço ainda. Adicione pelo menos um para precificar a proposta.
        </p>
      )}

      <div className="space-y-2">
        {items.map((it, idx) => {
          const subtotal = itemSubtotal({
            pricingMode: it.pricing_mode,
            hours: it.hours,
            hourlyRate: it.hourly_rate,
            fixedPrice: it.fixed_price,
            monthlyAmount: it.monthly_amount,
          });
          const linked = it.service_type_id
            ? serviceTypes.find((s) => s.id === it.service_type_id)
            : null;
          return (
            <div
              key={idx}
              className="space-y-2.5 rounded-xl border border-border bg-muted/30 p-3"
              data-testid="budget-item"
            >
              <div className="flex items-start gap-2">
                <span className="mt-2 w-5 shrink-0 text-center font-mono text-[11px] text-muted-foreground">
                  {idx + 1}
                </span>
                <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[1fr_auto]">
                  <Input
                    value={it.title}
                    onChange={(e) => update(idx, { title: e.target.value })}
                    placeholder={linked ? linked.name : "Nome do serviço"}
                    disabled={disabled}
                    aria-label={`Título do item ${idx + 1}`}
                  />
                  <Select
                    value={it.service_type_id ?? "__custom"}
                    onValueChange={(v) => {
                      if (v === "__custom") return update(idx, { service_type_id: null });
                      const st = serviceTypes.find((s) => s.id === v);
                      if (!st) return;
                      update(idx, {
                        ...itemFromServiceType(st, fallbackRate),
                        title: it.title.trim() ? it.title : st.name,
                      });
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      className="sm:w-[210px]"
                      aria-label={`Tipo de serviço do item ${idx + 1}`}
                    >
                      {/* Explicit label: Radix only resolves SelectValue from a mounted item, and the catalog loads after the value is set. */}
                      <SelectValue placeholder="Tipo de serviço">
                        {it.service_type_id
                          ? (linked?.name ?? "Tipo removido")
                          : "Item personalizado"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__custom">Item personalizado</SelectItem>
                      {serviceTypes.map((st) => (
                        <SelectItem key={st.id} value={st.id}>
                          {st.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!disabled && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      aria-label="Mover para cima"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => move(idx, 1)}
                      disabled={idx === items.length - 1}
                      aria-label="Mover para baixo"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => remove(idx)}
                      aria-label={`Remover item ${idx + 1}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-2 pl-7">
                <Tabs
                  value={it.pricing_mode}
                  onValueChange={(v) => {
                    if (!disabled) update(idx, { pricing_mode: v as PricingMode });
                  }}
                >
                  <TabsList className="h-8 p-0.5">
                    <TabsTrigger value="hourly" className="h-7 px-2.5 text-xs" disabled={disabled}>
                      Por hora
                    </TabsTrigger>
                    <TabsTrigger value="fixed" className="h-7 px-2.5 text-xs" disabled={disabled}>
                      Fixo
                    </TabsTrigger>
                    <TabsTrigger
                      value="recurring"
                      className="h-7 px-2.5 text-xs"
                      disabled={disabled}
                    >
                      Mensal
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                {it.pricing_mode === "hourly" ? (
                  <>
                    <div className="space-y-1">
                      <Label
                        htmlFor="budget-item-horas"
                        className="text-[11px] text-muted-foreground"
                      >
                        Horas
                      </Label>
                      <Input
                        id="budget-item-horas"
                        type="number"
                        min={0}
                        step={0.5}
                        className="h-8 w-24"
                        value={it.hours}
                        onChange={(e) => update(idx, { hours: Number(e.target.value) })}
                        disabled={disabled}
                        aria-label={`Horas do item ${idx + 1}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor="budget-item-valor-hora-r"
                        className="text-[11px] text-muted-foreground"
                      >
                        Valor/hora (R$)
                      </Label>
                      <Input
                        id="budget-item-valor-hora-r"
                        type="number"
                        min={0}
                        className="h-8 w-28"
                        value={it.hourly_rate}
                        onChange={(e) => update(idx, { hourly_rate: Number(e.target.value) })}
                        disabled={disabled}
                        aria-label={`Valor por hora do item ${idx + 1}`}
                      />
                    </div>
                  </>
                ) : it.pricing_mode === "fixed" ? (
                  <div className="space-y-1">
                    <Label
                      htmlFor="budget-item-preco-fixo-r"
                      className="text-[11px] text-muted-foreground"
                    >
                      Preço fixo (R$)
                    </Label>
                    <Input
                      id="budget-item-preco-fixo-r"
                      type="number"
                      min={0}
                      className="h-8 w-32"
                      value={it.fixed_price}
                      onChange={(e) => update(idx, { fixed_price: Number(e.target.value) })}
                      disabled={disabled}
                      aria-label={`Preço fixo do item ${idx + 1}`}
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label
                      htmlFor="budget-item-valor-mensal-r"
                      className="text-[11px] text-muted-foreground"
                    >
                      Valor mensal (R$)
                    </Label>
                    <Input
                      id="budget-item-valor-mensal-r"
                      type="number"
                      min={0}
                      className="h-8 w-32"
                      value={it.monthly_amount}
                      onChange={(e) => update(idx, { monthly_amount: Number(e.target.value) })}
                      disabled={disabled}
                      aria-label={`Valor mensal do item ${idx + 1}`}
                    />
                  </div>
                )}
                <div className="ml-auto text-right">
                  <div className="text-[11px] text-muted-foreground">
                    {it.pricing_mode === "recurring" ? "R$/mês" : "Subtotal"}
                  </div>
                  <div className="text-sm font-semibold" data-testid="item-subtotal">
                    {fmt(subtotal)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
